import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth, useUser } from '../auth/useAuth';
import { getChanceyApiToken } from '../api/authToken';
import { useApi } from '../api/client';
import type { ImportBirthdayResponse } from '../api/types';
import {
  DEFAULT_PREFS,
  sanitizeUserPrefs,
  type NotifPrefs,
  type UserPrefs,
} from './userPrefsModel';

export type { ExperienceMode, NotifPrefs, UserPrefs } from './userPrefsModel';
export { DEFAULT_PREFS, sanitizeUserPrefs } from './userPrefsModel';

const STORAGE_KEY = 'chancey_prefs';
const SAVE_DEBOUNCE_MS = 400;

function isValidBirthday(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value === '') return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return false;
  if (parsed > Date.now()) return false;
  return true;
}

function readLocal(): UserPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    const sane = sanitizeUserPrefs(parsed);
    const parsedGameId =
      parsed && typeof parsed === 'object'
        ? (parsed as { gameId?: unknown }).gameId
        : undefined;
    if (parsedGameId !== undefined && parsedGameId !== sane.gameId) writeLocal(sane);
    return sane;
  } catch {
    return DEFAULT_PREFS;
  }
}

function readLocalIfPresent(): UserPrefs | undefined {
  try {
    if (!localStorage.getItem(STORAGE_KEY)) return undefined;
    return readLocal();
  } catch {
    return undefined;
  }
}

function writeLocal(prefs: UserPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

function samePrefs(a: UserPrefs, b: UserPrefs): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export type UserPrefsPatch = Partial<Omit<UserPrefs, 'notif'>> & { notif?: Partial<NotifPrefs> };

export interface UserPrefsContextValue {
  prefs: UserPrefs;
  setPrefs: (next: UserPrefs) => void;
  setPartial: (patch: UserPrefsPatch) => void;
  importBirthday: () => Promise<ImportBirthdayResponse>;
}

const UserPrefsContext = createContext<UserPrefsContextValue | null>(null);

export const UserPrefsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const api = useApi();
  const [prefs, setPrefsState] = useState<UserPrefs>(() => readLocal());
  const prefsRef = useRef(prefs);
  const saveTimer = useRef<number | null>(null);
  const hydratedRemoteForUserId = useRef<string | null>(null);
  const birthdayImportKickedForUserId = useRef<string | null>(null);

  useEffect(() => {
    prefsRef.current = prefs;
  }, [prefs]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      hydratedRemoteForUserId.current = null;
      birthdayImportKickedForUserId.current = null;
      return;
    }
    if (hydratedRemoteForUserId.current === user.id) return;
    hydratedRemoteForUserId.current = user.id;
    const cacheAtRequestStart = prefsRef.current;
    api
      .get<{
        prefs?: unknown;
      }>('/v1/me/prefs')
      .then((body) => {
        if (!samePrefs(prefsRef.current, cacheAtRequestStart)) return;
        const local = readLocalIfPresent();
        if (body.prefs === undefined || body.prefs === null) {
          if (local) {
            const seeded = sanitizeUserPrefs(local);
            if (!samePrefs(prefsRef.current, seeded)) {
              prefsRef.current = seeded;
              setPrefsState(seeded);
            }
            writeLocal(seeded);
            void api.put('/v1/me/prefs', { prefs: seeded }).catch(() => {
              /* best-effort: cache remains until next sync */
            });
          }
          return;
        }
        const remote = sanitizeUserPrefs(body.prefs);
        setPrefsState((prev) => {
          if (samePrefs(prev, remote)) return prev;
          prefsRef.current = remote;
          writeLocal(remote);
          return remote;
        });
      })
      .catch(() => {
        /* best-effort: local cache remains until next sync */
      });
  }, [api, isLoaded, user]);

  const persist = useCallback(
    (next: UserPrefs) => {
      writeLocal(next);
      if (saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      if (!user) return;
      saveTimer.current = window.setTimeout(() => {
        api
          .put('/v1/me/prefs', { prefs: next })
          .catch(() => {
            /* best-effort: local cache remains until next sync */
          });
      }, SAVE_DEBOUNCE_MS);
    },
    [api, user]
  );

  useEffect(() => {
    return () => {
      if (saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
      }
    };
  }, []);

  const setPrefs = useCallback(
    (next: UserPrefs) => {
      const sane = sanitizeUserPrefs(next);
      prefsRef.current = sane;
      setPrefsState(sane);
      persist(sane);
    },
    [persist]
  );

  const setPartial = useCallback(
    (patch: UserPrefsPatch) => {
      setPrefsState((prev) => {
        const next: UserPrefs = sanitizeUserPrefs({
          ...prev,
          ...patch,
          notif: { ...prev.notif, ...(patch.notif ?? {}) },
        });
        prefsRef.current = next;
        persist(next);
        return next;
      });
    },
    [persist]
  );

  /**
   * Import birthday from a user-specified provider (currently Google via our Worker).
   * Called both by the silent auto-import below and by the "Import from Google" button in Settings.
   */
  const importBirthday = useCallback(async (): Promise<ImportBirthdayResponse> => {
    const base = process.env.REACT_APP_API_BASE_URL ?? '';
    if (!base) {
      return { birthday: null, source: null, reason: 'provider_error' };
    }
    const token = await getChanceyApiToken(getToken);
    if (!token) {
      return { birthday: null, source: null, reason: 'provider_error' };
    }
    try {
      const res = await fetch(`${base}/v1/me/import-birthday`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        return { birthday: null, source: null, reason: 'provider_error' };
      }
      const body = (await res.json()) as ImportBirthdayResponse;
      const valid = isValidBirthday(body.birthday ?? '');
      const birthdayValue = valid && body.birthday ? body.birthday : '';
      if (birthdayValue) {
        setPrefsState((prev) => {
          const next = sanitizeUserPrefs({ ...prev, birthday: birthdayValue, birthdayImported: true });
          prefsRef.current = next;
          persist(next);
          return next;
        });
      } else {
        setPrefsState((prev) => {
          const next = sanitizeUserPrefs({ ...prev, birthdayImported: true });
          prefsRef.current = next;
          persist(next);
          return next;
        });
      }
      return body;
    } catch {
      return { birthday: null, source: null, reason: 'provider_error' };
    }
  }, [getToken, persist]);

  // Silent auto-import after first sign-in: fire once per user, gated by `birthdayImported`.
  useEffect(() => {
    if (
      !isLoaded ||
      !user ||
      hydratedRemoteForUserId.current !== user.id ||
      birthdayImportKickedForUserId.current === user.id
    ) return;
    if (prefs.birthdayImported || prefs.birthday) return;
    birthdayImportKickedForUserId.current = user.id;
    importBirthday().catch(() => {
      /* best-effort */
    });
  }, [isLoaded, user, prefs.birthdayImported, prefs.birthday, importBirthday]);

  const value = useMemo<UserPrefsContextValue>(
    () => ({ prefs, setPrefs, setPartial, importBirthday }),
    [prefs, setPrefs, setPartial, importBirthday]
  );

  return <UserPrefsContext.Provider value={value}>{children}</UserPrefsContext.Provider>;
};

export function useUserPrefs(): UserPrefsContextValue {
  const ctx = useContext(UserPrefsContext);
  if (!ctx) {
    throw new Error('useUserPrefs must be used within UserPrefsProvider');
  }
  return ctx;
}
